<script setup lang="ts">
import { i18n } from '@/locales/i18n'
import { getScreenUtils } from '@/components/lib/GwUtils/utils_screen'
import CardGroupesPicker from '@/components/CardGroupesPicker/CardGroupesPicker.vue'

import TagsManager from '@/components/TagsManager/TagsManager.vue'
import GwBtns from '@/components/lib/GwBtns.vue'
import CardsPicker from '@/components/CardsPicker/CardsPicker.vue'
import Datepicker from '@/components/DataTables/Filters/Datepicker/Datepicker.vue'
import GwColorPicker from '@/components/lib/GwColorPicker.vue'
import GwPaginatedSearch from '@/components/DataTables/Filters/new/GwPaginatedSearch.vue'
import GwRangeSelect from '@/components/lib/GwRangeSelect.vue'
import GwFilterBtnsItem from '@/components/DataTables/Filters/new/GwFilterBtnsItem.vue'
import UnSwitcherNew from '@/components/Switchers/UnSwitcher_new.vue'

interface FieldProps {
  field_name: string
  filter_type?: string
  filter_ui_type?: string
  available_choices?: string[]
  range_keys?: string[]
  tag_type?: string
}

export interface FiltersOptionsProps {
  apiKeys?: string[]
  category?: string
  child_field?: any
  display_key?: string
  fetchOptions?: boolean
  field?: FieldProps
  field_name?: string
  filterPart?: any
  id: string
  idKey?: string
  isMultiple?: boolean
  isRange?: boolean
  isSingle?: boolean
  options: string[]
  optionsFetch?: any
  parent_empty?: boolean
  picker?: string
  pickerProps?: any
  rangeValues?: any[]
  selectedOptions?: any[]
  selection?: string
  title?: string
  urlVals?: any[]
  value?: string
  values?: any[]
}

interface GwFilterPickerComponentProps {
  field: FiltersOptionsProps
  btnsCssClass?: string
  btnsCssStyle?: string
}
withDefaults(defineProps<GwFilterPickerComponentProps>(), {
  btnsCssClass: 'gwbtns_vertical_white',
  btnsCssStyle: 'padding: 0rem',
})
const emit = defineEmits(['update:field'])

const {
  global: { t },
} = i18n

const { mobile } = getScreenUtils()

const handleUpdate = ({ field, item, value, values, rangeValues }: Record<string, any>) => {
  emit('update:field', { field, item, value, values, rangeValues })
}
</script>
<template>
  <div class="gw_filter_picker gwffpckr">
    <GwBtns
      v-if="field.picker === 'GwBtns'"
      :style="`max-height: clamp(1px, 44rem, 100vh);${btnsCssStyle || ''};`"
      :options="field.options"
      :values="field.values || []"
      :value="field.value || ''"
      :idKey="field.idKey"
      :tstPrefix="`filterpicker_${field.field_name}`"
      :class="btnsCssClass"
      class="flex-1"
      @click.stop="
        (_e, item) => {
          handleUpdate({ field, item })
        }
      "
    >
      <template #before>
        <div
          v-if="field.child_field && field.child_field.picker === 'anyall'"
          class="d-flex text-medium line-height_1 clr-grey z-index_4 mr-auto"
          style="gap: 0.05rem; padding: 0 0.5rem 0.25rem 0.5rem; min-width: 7rem"
          data-gwmenu="noclose"
        >
          <div class="my-auto">
            {{ t('anyall_tags_switch.match') }}
          </div>
          <UnSwitcherNew
            trigger="click"
            :isInside="true"
            :hasOpener="true"
            :closeOnContentClick="true"
            :ignoreDataGwmenu="true"
            :options="['any', 'all']"
            :tab="field.child_field.value || 'any'"
            style="--uns-opener-padding: 0 1.25rem 0 0.25rem"
            class="clr-grey my-auto weight_500"
            @click="item => handleUpdate({ field: field.child_field, item })"
          >
            <template #selected="{ item }">
              <span class="weigh_500 clr-grey">
                {{ t(`common.${item}`) }}
              </span>
            </template>
            <template #default="{ item }">
              {{ t(`common.${item}`) }}
            </template>
          </UnSwitcherNew>
        </div>
      </template>
      <template #default="{ item }">
        <GwFilterBtnsItem
          :field
          :item="item"
        ></GwFilterBtnsItem>
      </template>
    </GwBtns>

    <!-- SELECT WITH SEARCH -->
    <GwPaginatedSearch
      v-else-if="field.picker === 'GwPaginatedSearch'"
      style="max-height: clamp(1px, 44rem, 100vh)"
      v-bind="field.pickerProps"
      :searchKey="field.pickerProps.searchKey || 'search'"
    >
      <template #results="{ items }">
        <GwBtns
          :class="btnsCssClass"
          :style="btnsCssStyle"
          :options="items"
          :values="field.values || []"
          :value="field.value || ''"
          :idKey="field.idKey"
          :tstPrefix="`filterpicker_${field.field_name}`"
          @click.stop="
            (_e, item) => {
              handleUpdate({ field, item })
            }
          "
        >
          <template #default="{ item }">
            <GwFilterBtnsItem
              :field
              :item="item"
              tstPrefix="filterpicker"
            ></GwFilterBtnsItem>
          </template>
        </GwBtns>
      </template>
    </GwPaginatedSearch>

    <!-- RANGE-KEYS -->
    <Datepicker
      v-if="field.picker === 'Datepicker'"
      :immediateConfirm="true"
      :selectedDate="{
        date_from: field.rangeValues?.[0], //
        date_to: field.rangeValues?.[1],
      }"
      @update="
        ({ date_from, date_to }) =>
          handleUpdate({
            field, //
            rangeValues: [
              date_from, //
              date_to,
            ],
          })
      "
    />
    <GwRangeSelect
      v-else-if="field.picker === 'GwRangeSelect'"
      :from="field.rangeValues?.[0]"
      :to="field.rangeValues?.[1]"
      :options="field.options"
      @update="
        ({ from, to }) =>
          handleUpdate({
            field, //
            rangeValues: [
              from, //
              to,
            ],
          })
      "
    >
      <template #title_from>
        <div class="mb-2 weight_500 clr-grey">{{ t('common.min') }}</div></template
      >
      <template #title_to>
        <div class="mb-2 weight_500 clr-grey">{{ t('common.max') }}</div>
      </template>
    </GwRangeSelect>

    <!-- MULTIPLE -->
    <CardGroupesPicker
      v-else-if="field.picker === 'CardGroupesPicker'"
      style="min-width: clamp(1px, 28rem, 100vw)"
      :groups="field.values"
      @update:groups="groups => handleUpdate({ field, values: groups })"
    />
    <GwColorPicker
      v-else-if="field.picker === 'GwColorPicker'"
      :values="field.values"
      :colors="field.options"
      @update:hex="hex => handleUpdate({ field, item: hex })"
    >
      <div
        v-if="!field.options.length"
        class="clr-grey text-medium weight_500 pa-2 text-center"
        style="max-width: 12rem"
      >
        {{ t('messages.no_colors_in_uploaded_hands') }}
      </div>
    </GwColorPicker>
    <!-- SINGLE -->
    <CardsPicker
      v-else-if="field.picker === 'CardsPicker'"
      cardSize="medium"
      :hasClearCards="true"
      :board="field.value || ''"
      @update:board="board => handleUpdate({ field, item: board })"
    />

    <!-- TAGS -->
    <TagsManager
      v-if="field.picker === 'TagsManager'"
      style="padding: 0"
      :tagIds="field.values || []"
      :allTags="field.options"
      :tagType="field.field?.tag_type || ''"
      :hasEdit="true"
      :anyAll="field.child_field.value || 'any'"
      :inputAutofocus="!mobile"
      @click.stop
      @click:tag="
        tag => {
          handleUpdate({ field, item: tag.id })
        }
      "
      @update:anyall="val => handleUpdate({ field: field.child_field, item: val })"
    >
    </TagsManager>
  </div>
</template>
<style lang="scss" scoped>
.gw_filter_picker {
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
}
</style>
